import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { euro } from "@/lib/format";
import { Plus, Pencil } from "lucide-react";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function GestioneServizi() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-services"], queryFn: async () => (await api.get("/services?only_active=false")).data });
  const services = data?.services || [];
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", icon: "Stethoscope", base_price: "", active: true });

  const openNew = () => { setEdit("new"); setForm({ name: "", description: "", icon: "Stethoscope", base_price: "", active: true }); };
  const openEdit = (s) => { setEdit(s); setForm({ name: s.name, description: s.description, icon: s.icon || "Stethoscope", base_price: (s.base_price_cents / 100).toString(), active: s.active }); };

  const save = async () => {
    const payload = { name: form.name, slug: edit === "new" ? slugify(form.name) : edit.slug, description: form.description, icon: form.icon, base_price_cents: Math.round(Number(form.base_price || 0) * 100), active: form.active };
    try {
      if (edit === "new") await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${edit.id}`, payload);
      toast.success("Servizio salvato."); setEdit(null); qc.invalidateQueries({ queryKey: ["admin-services"] }); qc.invalidateQueries({ queryKey: ["services"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Gestione servizi">
      <div className="flex justify-end mb-4"><Button data-testid="new-service" onClick={openNew} className="bg-[#087A73] text-white rounded-xl h-11 px-5"><Plus className="mr-1 h-4 w-4" /> Nuovo servizio</Button></div>
      {isLoading ? <LoadingBlock /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {services.map((s) => (
            <Card key={s.id} data-testid={`admin-service-${s.slug}`}>
              <div className="flex justify-between items-start">
                <div><p className="font-bold text-[#102A2A]">{s.name}</p><p className="text-sm text-[#5C6B68] mt-1 line-clamp-2">{s.description}</p></div>
                <StatusBadge status={s.active ? "verificato" : "annullata"} label={s.active ? "Attivo" : "Disattivo"} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[#087A73] font-bold">da {euro(s.base_price_cents)}</span>
                <Button data-testid={`edit-service-${s.slug}`} variant="outline" onClick={() => openEdit(s)} className="rounded-lg h-9 px-3 text-sm border-2 border-[#D2E3DB]"><Pencil className="mr-1 h-4 w-4" /> Modifica</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>{edit === "new" ? "Nuovo servizio" : "Modifica servizio"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="service-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Prezzo indicativo (€)</Label><Input data-testid="service-price" type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> <span className="text-sm text-[#5C6B68]">Attivo</span></label>
            <Button data-testid="service-save" onClick={save} className="w-full bg-[#087A73] text-white rounded-xl h-11">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
